import { useState, useEffect, useCallback } from 'react';

interface TourStep {
  id: string;
  target: string;
  title: string;
  description: string;
  icon: string;
  position: 'top' | 'bottom' | 'left' | 'right';
  offset?: { x?: number; y?: number };
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    target: '',
    title: 'Welcome to AI Image Generator!',
    description: 'Let\'s take a quick 30-second tour so you can start creating stunning images right away. You can skip this any time.',
    icon: 'ri-sparkling-2-fill',
    position: 'bottom',
  },
  {
    id: 'prompt',
    target: '[data-tour="prompt"]',
    title: 'Describe Your Vision',
    description: 'This is where the magic starts. Be specific — describe subjects, lighting, mood, colors, and environment for the best results. You can also hit "✨ Enhance Prompt" to let AI upgrade your description automatically.',
    icon: 'ri-edit-2-line',
    position: 'right',
  },
  {
    id: 'style',
    target: '[data-tour="style"]',
    title: 'Pick an Art Style',
    description: 'Choose from 8 styles: Realistic, Anime, Digital Art, Oil Painting, Watercolor, Sketch, 3D Render, or Pixel Art. Each completely transforms the look and feel of your output.',
    icon: 'ri-palette-line',
    position: 'right',
  },
  {
    id: 'aspect',
    target: '[data-tour="aspect"]',
    title: 'Set the Aspect Ratio',
    description: 'Match the format to your use case — 1:1 for social posts, 16:9 for desktop wallpapers, 9:16 for mobile/stories, 4:3 for presentations.',
    icon: 'ri-crop-line',
    position: 'right',
  },
  {
    id: 'generate',
    target: '[data-tour="generate"]',
    title: 'Generate & Compare!',
    description: 'Hit Generate Image to create your first image. You can then hit "Compare Variations" to generate a second version and compare them side-by-side. You get 3 free generations — no sign-up needed.',
    icon: 'ri-image-ai-line',
    position: 'top',
  },
];

const TOUR_KEY = 'aiImage_onboardingDone';

interface TooltipRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface OnboardingTourProps {
  darkMode: boolean;
}

export default function OnboardingTour({ darkMode }: OnboardingTourProps) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [targetRect, setTargetRect] = useState<TooltipRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    try {
      const done = localStorage.getItem(TOUR_KEY);
      if (!done) {
        setTimeout(() => setVisible(true), 800);
      }
    } catch { /* noop */ }
  }, []);

  const measureTarget = useCallback((selector: string) => {
    if (!selector) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    } else {
      setTargetRect(null);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const current = TOUR_STEPS[step];
    measureTarget(current.target);

    const handler = () => measureTarget(current.target);
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [step, visible, measureTarget]);

  useEffect(() => {
    if (!targetRect) {
      setTooltipStyle({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
      return;
    }
    const current = TOUR_STEPS[step];
    const TOOLTIP_W = 340;
    const TOOLTIP_H = 200;
    const PAD = 16;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    let top = 0;
    let left = 0;

    if (current.position === 'right') {
      top = targetRect.top + targetRect.height / 2 - TOOLTIP_H / 2;
      left = targetRect.left + targetRect.width + PAD;
      if (left + TOOLTIP_W > vw) {
        left = targetRect.left - TOOLTIP_W - PAD;
      }
    } else if (current.position === 'left') {
      top = targetRect.top + targetRect.height / 2 - TOOLTIP_H / 2;
      left = targetRect.left - TOOLTIP_W - PAD;
    } else if (current.position === 'top') {
      top = targetRect.top - TOOLTIP_H - PAD;
      left = targetRect.left + targetRect.width / 2 - TOOLTIP_W / 2;
    } else {
      top = targetRect.top + targetRect.height + PAD;
      left = targetRect.left + targetRect.width / 2 - TOOLTIP_W / 2;
    }

    top = Math.max(PAD, Math.min(top, vh - TOOLTIP_H - PAD));
    left = Math.max(PAD, Math.min(left, vw - TOOLTIP_W - PAD));

    setTooltipStyle({ top, left, width: TOOLTIP_W });
  }, [targetRect, step]);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(TOUR_KEY, '1'); } catch { /* noop */ }
  };

  const next = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      dismiss();
    }
  };

  const prev = () => setStep(s => Math.max(0, s - 1));

  if (!visible) return null;

  const current = TOUR_STEPS[step];
  const isFirst = step === 0;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <>
      {/* Dim overlay */}
      <div className="fixed inset-0 z-[9000] pointer-events-none" style={{ background: 'rgba(0,0,0,0.55)' }} />

      {/* Spotlight cutout */}
      {targetRect && (
        <div
          className="fixed z-[9001] pointer-events-none rounded-xl ring-4 ring-offset-0"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
            borderRadius: 14,
          }}
        />
      )}

      {/* Click-through blocker except on tooltip */}
      <div className="fixed inset-0 z-[9001]" onClick={next} />

      {/* Tooltip card */}
      <div
        className={`fixed z-[9002] rounded-2xl shadow-2xl p-5 select-none ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}
        style={{ ...tooltipStyle, maxWidth: 340 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Step dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {TOUR_STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === step ? 'w-6 bg-purple-500' : i < step ? 'w-3 bg-purple-300' : 'w-3 bg-gray-200 dark:bg-gray-600'}`}
            />
          ))}
          <span className={`ml-auto text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>
            {step + 1} / {TOUR_STEPS.length}
          </span>
        </div>

        {/* Icon + Title */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <i className={`${current.icon} text-white text-lg`}></i>
          </div>
          <div>
            <h4 className={`font-bold text-base leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {current.title}
            </h4>
          </div>
        </div>

        <p className={`text-sm leading-relaxed mb-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {current.description}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isFirst && (
            <button
              onClick={prev}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
            >
              <i className="ri-arrow-left-line mr-1"></i>Back
            </button>
          )}
          <button
            onClick={dismiss}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
          >
            Skip tour
          </button>
          <button
            onClick={next}
            className="ml-auto px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white text-sm font-semibold rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            {isLast ? (
              <><i className="ri-check-line mr-1"></i>Let&apos;s go!</>
            ) : (
              <>Next<i className="ri-arrow-right-line ml-1"></i></>
            )}
          </button>
        </div>

        {/* Pulse hint */}
        {!isFirst && (
          <p className={`text-xs mt-3 text-center ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>
            Click anywhere outside to advance
          </p>
        )}
      </div>
    </>
  );
}
